import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { APIEntityType, ArdoqId, Features } from '@ardoq/api-types';
import { type ReferenceComponents } from './referenceComponentsInterface';
import { toIds } from '@ardoq/core';
import { GetContextMenuOptionsArguments } from '@ardoq/context-menu';
import { DropdownItem } from '@ardoq/dropdown-menu';
import { TrackedContextMenus, getTrackingFunction } from './tracking';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getDeleteReferenceMenuItem,
  getDropdownDivider,
  getEditPropertiesMenuItem,
  getFilterReferenceMenuItem,
  getHistoryMenuItem,
  getMergeEntitiesMenuItem,
  getReferenceNavigateToMenuItem,
  getReverseReferenceMenuItem,
  getToggleLockMenuItem,
} from './menuItems';
import { checkHasWriteAccess } from './utils';
import { referenceAccessControlOperation } from 'resourcePermissions/accessControlHelpers/reference';
import { getFeatureContext } from 'streams/featureContext$';
import { getActiveScenarioState } from 'streams/activeScenario/activeScenario$';
import { activeScenarioOperations } from '@ardoq/core';
import { scenarioAccessControlInterface } from 'resourcePermissions/accessControlHelpers/scenarioAccessControlInterface';
import { PermissionContext } from '@ardoq/access-control';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { type ReferenceCreationMenuState } from 'streams/referenceCreationMenuState$';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { type I18nLocale } from '@ardoq/i18n';
import { focusedChromeNow } from 'scope/focusedChromeNow';
import { focusedChromeOperations } from 'scope/focusedChromeOperations';

type GetReferenceMenuArgs = {
  referenceComponents: ReferenceComponents;
  workspaceIds: ArdoqId[];
  hasLockedReferences: boolean;
  isViewpointMode: boolean;
  permissionContext: PermissionContext;
  workspaceAndModel: WorkspaceAndModelLookup;
  referenceCreation: ReferenceCreationMenuState;
  i18nLocale: I18nLocale;
} & GetContextMenuOptionsArguments;
export const getReferenceMenu = ({
  referenceComponents,
  workspaceIds,
  hasLockedReferences,
  target,
  isViewpointMode,
  permissionContext,
  workspaceAndModel,
  referenceCreation,
  i18nLocale,
}: GetReferenceMenuArgs): DropdownItem[] | null => {
  const { references, sourceComponentsById, targetComponentsById } =
    referenceComponents;
  const referenceIds = toIds(references);
  if (isViewpointMode && isDiscoverJourney()) {
    return null;
  }
  const menuItemReferenceTrackingFunction = getTrackingFunction(
    target,
    TrackedContextMenus.REFERENCE_CONTEXT_MENU
  );

  const hasWriteAccess = checkHasWriteAccess({
    workspaceIds,
    permissionContext,
    references,
    referenceSourceComponentsById: sourceComponentsById,
  });
  const isSingleReference = referenceIds.length === 1;

  if (!isSingleReference && !hasWriteAccess) {
    // no context menu for multiselect with no write access
    return null;
  }

  const allowsContextNavigation =
    focusedChromeOperations.allowsContextNavigation(focusedChromeNow());
  const referenceNavigateToMenuItem =
    !allowsContextNavigation || isViewpointMode
      ? null
      : getReferenceNavigateToMenuItem(
          references[0],
          referenceComponents.fullPathNamesById,
          menuItemReferenceTrackingFunction,
          i18nLocale
        );
  const filterReferenceMenuItem = isViewpointMode
    ? null
    : getFilterReferenceMenuItem({
        reference: references[0],
        endpoints: {
          source: sourceComponentsById[references[0].source] ?? null,
          target: targetComponentsById[references[0].target] ?? null,
        },
        fullPathNamesById: referenceComponents.fullPathNamesById,
        workspaceAndModel,
        trackingFn: menuItemReferenceTrackingFunction,
        i18nLocale,
      });
  if (!hasWriteAccess) {
    const menu = [
      ...(referenceNavigateToMenuItem
        ? [referenceNavigateToMenuItem, getDropdownDivider()]
        : []),
      filterReferenceMenuItem,
    ].filter(isTruthy);

    return menu.length ? menu : null;
  }

  const activeScenarioState = getActiveScenarioState();
  const canEditScenario =
    !activeScenarioOperations.isInScenarioMode(activeScenarioState) ||
    scenarioAccessControlInterface.canEditActiveScenario(
      permissionContext,
      activeScenarioState
    );
  const featureContext = getFeatureContext();
  const canEditAllReferencesTargets =
    canEditScenario &&
    references.every(reference => {
      const targetComponent = targetComponentsById[reference.target];
      if (!targetComponent) {
        return false;
      }
      return referenceAccessControlOperation.canEditReferencesTargetComponent({
        reference,
        components: [targetComponent],
        permissionContext,
        featureContext,
      });
    });

  const historyMenuItem =
    isSingleReference &&
    getHistoryMenuItem(
      referenceIds[0],
      APIEntityType.REFERENCE,
      menuItemReferenceTrackingFunction,
      i18nLocale
    );

  // KFQ12026
  const isCanvasPrototype = hasFeature(Features.CANVAS_PROTOTYPE);

  const editPropertiesMenuItem = getEditPropertiesMenuItem(
    [],
    referenceIds,
    menuItemReferenceTrackingFunction,
    i18nLocale,
    isViewpointMode
  );

  const deleteReferenceMenuItem = getDeleteReferenceMenuItem(
    referenceIds,
    menuItemReferenceTrackingFunction,
    i18nLocale,
    isCanvasPrototype
  );

  if (isCanvasPrototype && isViewpointMode) {
    const isMergeEntitiesMenuItemVisible =
      !isSingleReference && !hasLockedReferences;

    const menu = [
      !hasLockedReferences && editPropertiesMenuItem,
      historyMenuItem,
      deleteReferenceMenuItem,
      isMergeEntitiesMenuItemVisible && getDropdownDivider(),
      isMergeEntitiesMenuItemVisible &&
        getMergeEntitiesMenuItem(
          referenceIds,
          APIEntityType.REFERENCE,
          menuItemReferenceTrackingFunction,
          i18nLocale
        ),
    ].filter(isTruthy);
    return menu.length > 0 ? menu : null;
  }

  const editItems = !hasLockedReferences
    ? [
        editPropertiesMenuItem,
        !isViewpointMode &&
          getReverseReferenceMenuItem({
            referenceComponents,
            workspaceAndModel,
            referenceCreation,
            trackingFn: menuItemReferenceTrackingFunction,
            canEditAllReferencesTargets,
            i18nLocale,
          }),
        deleteReferenceMenuItem,
        getDropdownDivider(),
      ]
    : [];

  const toggleLockMenuItem = isViewpointMode
    ? null
    : getToggleLockMenuItem({
        components: [],
        references,
        trackingFn: menuItemReferenceTrackingFunction,
        i18nLocale,
        permissionContext,
      });
  const menu = [
    ...editItems,
    historyMenuItem,
    toggleLockMenuItem,
    historyMenuItem || toggleLockMenuItem ? getDropdownDivider() : null,
    ...(isSingleReference
      ? [
          referenceNavigateToMenuItem,
          referenceNavigateToMenuItem ? getDropdownDivider() : null,
          filterReferenceMenuItem,
        ]
      : [
          !hasLockedReferences &&
            !isViewpointMode &&
            getMergeEntitiesMenuItem(
              referenceIds,
              APIEntityType.REFERENCE,
              menuItemReferenceTrackingFunction,
              i18nLocale
            ),
        ]),
  ].filter(isTruthy);

  return menu.length ? menu : null;
};
